import React from 'react';
import { Stack, Typography, TableCell, Tooltip } from '@mui/material';
import { PoiIconsList } from './PoiIconsList';
import { abyssColors } from '@/theme/colors';
import { formatEnumForDisplay, getPoiTemplateKey } from '@/utils';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { useAtomValue } from 'jotai';
import * as analysisState from '@/components/Analysis/state';

interface PoiIconsCellProps {
  poiCounts: Array<{ poiType: string; count: number; poiTemplateKey?: string | null | undefined }>;
  isSelected: boolean;
  totalCount?: number | null;
}


export const PoiIconsCell: React.FC<PoiIconsCellProps> = ({
  poiCounts,
  isSelected,
  totalCount,
}) => {
  const allTemplates = useAtomValue(analysisState.allPointOfInterestTemplates) || [];
  const cellStyles = {
    backgroundColor: isSelected ? 'rgba(236,239,243,255)' : 'white',
    border: '1px solid rgba(224, 224, 224, 224)',
    padding: '8px 16px',
    width: '25rem',
    minWidth: '25rem',
    maxWidth: '40rem',
    position: 'relative',
    zIndex: 0, // Ensure POI column stays behind sticky columns
    overflow: 'hidden', // Prevent content from overflowing into sticky column
  } as React.CSSProperties;

  if (!poiCounts || poiCounts.length === 0) {
    return (
      <TableCell style={cellStyles}>
        <Typography fontSize="1.4rem" fontWeight={400} color={abyssColors.primary[300]}>
          {NOT_APPLICABLE_LABEL}
        </Typography>
      </TableCell>
    );
  }

  // Use totalCount if available, otherwise calculate from poiCounts
  let displayCount = poiCounts.reduce((sum, poi) => sum + poi.count, 0);
  if (!displayCount) {
    displayCount = totalCount ?? 0;
  }
  // Format tooltip as: POI_TYPE_1_NAME(POI_TYPE_1_COUNT), POI_TYPE_2_NAME(POI_TYPE_2_COUNT), etc.
  const tooltipText = poiCounts
    .map((poi) => {
      const normalizedPoiType = (poi.poiType || '').toString().toLowerCase();
      const normalizedPoiTemplateKey = (poi.poiTemplateKey || '').toString().toLowerCase();

      const matchedTemplate = allTemplates.find((template) => {
        const templateKey = (getPoiTemplateKey(template) ?? '').toString().toLowerCase();
        if (!templateKey) return false;
        return (
          templateKey === normalizedPoiTemplateKey ||
          templateKey === normalizedPoiType
        );
      });

      const displayName = matchedTemplate ? matchedTemplate.name : formatEnumForDisplay(poi.poiType);
      return `${displayName} (${poi.count})`;
    })
    .join(' | ');

  return (
    <Tooltip title={tooltipText} arrow>
      <TableCell style={cellStyles}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          gap={3}
          sx={{
            maxWidth: '100%',
            overflow: 'hidden', // Ensure icons don't overflow
          }}
        >
          {/* Left side: Total count display */}
          <Typography
            fontSize="1.4rem"
            fontWeight={400}
            color={abyssColors.primary[500]}
            sx={{ flexShrink: 0 }}
          >
            {displayCount}
          </Typography>

          {/* Right side: Stacked POI icons */}
          <PoiIconsList poiItems={poiCounts} size={2.2} spacing={-0.8} />
        </Stack>
      </TableCell>
    </Tooltip>
  );
};
